import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/context/AuthContext";
import { Bug, BookOpen, Layers, Zap, CheckSquare, Loader2, Filter, ChevronDown, RefreshCw, X, GitBranch } from "lucide-react";

const KANBAN_COLS = [
  { key: "todo",   label: "Por hacer",   statuses: ["Nuevo","New","Backlog","backlog"],               newStatus: "Nuevo",  headerCls: "bg-blue-50 text-blue-700",   dotCls: "bg-blue-400",   borderCls: "border-blue-200" },
  { key: "doing",  label: "En curso",    statuses: ["Activo","Active","Analyzing","In Progress"],      newStatus: "Activo", headerCls: "bg-amber-50 text-amber-700",  dotCls: "bg-amber-400",  borderCls: "border-amber-200" },
  { key: "review", label: "En revisión", statuses: ["Resolved","Resuelto"],                           newStatus: "Resuelto",headerCls:"bg-purple-50 text-purple-700",dotCls: "bg-purple-400", borderCls: "border-purple-200" },
  { key: "done",   label: "Cerrado",     statuses: ["Closed","closed","Cerrado","Done","Completado","Removed"], newStatus: "Cerrado", headerCls: "bg-green-50 text-green-700", dotCls: "bg-green-400",  borderCls: "border-green-200" },
];

const typeIcon = { Epic: Layers, Feature: Zap, "User Story": BookOpen, Bug: Bug, Task: CheckSquare, Tarea: CheckSquare };
const priorityBadge = { critica: "bg-red-100 text-red-700 font-bold", alta: "bg-orange-100 text-orange-700", media: "bg-yellow-100 text-yellow-700", baja: "bg-gray-100 text-gray-500" };
const PRIORITY_ORDER = { critica: 0, alta: 1, media: 2, baja: 3 };

export default function Backlog() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [filters, setFilters] = useState({ project_id: "", priority: "", source: "", sprint_id: "", search: "" });
  const [showFilters, setShowFilters] = useState(false);
  // Iter313 · O03 — avisar si el backlog supera el límite de 2.000 work
  // items devueltos (antes se truncaba en silencio sin explicación).
  const [truncatedInfo, setTruncatedInfo] = useState(null); // {total, shown}
  // P01 (roadmap-valoriza-revision-despliegue-2026-10-12, E01) · contadores
  // reales sobre TODA la población filtrada (no sólo los 2.000 cargados).
  const [stats, setStats] = useState(null);
  // P01 · resultado de búsqueda server-side (cubre toda la población, no
  // sólo los ítems ya cargados en el Kanban).
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.project_id) params.project_id = f.project_id;
      if (f.priority) params.priority = f.priority;
      if (f.source) params.source = f.source;
      if (f.sprint_id) params.sprint_id = f.sprint_id;
      const [itemsRes, projRes, sprintsRes, statsRes] = await Promise.all([
        axios.get(`${API}/backlog`, { params }),
        axios.get(`${API}/projects`),
        axios.get(`${API}/sprints`),
        axios.get(`${API}/backlog/stats`, { params }),
      ]);
      setItems(itemsRes.data);
      const totalCount = Number(itemsRes.headers?.["x-total-count"] || itemsRes.data.length);
      const truncated = itemsRes.headers?.["x-truncated"] === "1";
      setTruncatedInfo(truncated ? { total: totalCount, shown: itemsRes.data.length } : null);
      setProjects(projRes.data);
      setSprints(sprintsRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  // P01 · búsqueda server-side con debounce: cubre toda la población
  // filtrada, no sólo el corte de 2.000 ya cargado.
  useEffect(() => {
    const q = filters.search.trim();
    if (!q) { setSearchResult(null); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const params = { q };
        if (filters.project_id) params.project_id = filters.project_id;
        if (filters.priority) params.priority = filters.priority;
        if (filters.source) params.source = filters.source;
        if (filters.sprint_id) params.sprint_id = filters.sprint_id;
        const { data } = await axios.get(`${API}/backlog/search`, { params });
        setSearchResult(data);
      } catch (e) { console.error(e); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [filters.search, filters.project_id, filters.priority, filters.source, filters.sprint_id]);

  const handleDrop = async (colKey) => {
    if (!dragId) return;
    const col = KANBAN_COLS.find(c => c.key === colKey);
    if (!col) return;
    const item = items.find(i => i.id === dragId);
    if (!item || col.statuses.includes(item.status)) { setDragId(null); return; }
    try {
      await axios.put(`${API}/workitems/${dragId}`, { status: col.newStatus });
      setItems(prev => prev.map(i => i.id === dragId ? { ...i, status: col.newStatus } : i));
    } catch (e) { console.error(e); }
    setDragId(null);
  };

  const applyFilters = (f) => { setFilters(f); setShowFilters(false); load(f); };
  const resetFilters = () => { const e = { project_id: "", priority: "", source: "", sprint_id: "", search: "" }; applyFilters(e); };
  const hasFilters = filters.project_id || filters.priority || filters.source || filters.sprint_id;

  // P01 · con búsqueda activa se usa el resultado server-side (cubre toda
  // la población filtrada), no sólo los ítems ya cargados en el Kanban.
  const displayItems = filters.search ? (searchResult?.items || []) : items;

  // P01 · contadores de cabecera: usan /backlog/stats (población COMPLETA
  // filtrada en MongoDB), no items.length (sólo lo cargado en el Kanban).
  const realBacklog = filters.search
    ? displayItems.filter(i => i.source === "backlog_csv" || i.source === "backlog_v2")
    : [];
  const sprintItems = filters.search ? displayItems.filter(i => i.sprint_id) : [];

  return (
    <div className="space-y-4 max-w-full" data-testid="backlog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">{t("backlog.title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
            {/* P01 · estos contadores vienen de /backlog/stats (población
                COMPLETA filtrada), no de items.length (sólo lo cargado en
                el Kanban) — así concilian siempre con el corte real. */}
            <span data-testid="backlog-total-real">{stats ? stats.total.toLocaleString("es-ES") : "…"} items reales</span>
            {stats?.real_backlog > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{stats.real_backlog.toLocaleString("es-ES")} {t("layout.backlog").toLowerCase()}</span>}
            {stats?.with_sprint > 0 && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><GitBranch size={10}/>{stats.with_sprint.toLocaleString("es-ES")} · {t("layout.sprints").toLowerCase()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Project quick filter — always visible */}
          <select
            value={filters.project_id}
            onChange={e => { const f = { ...filters, project_id: e.target.value }; setFilters(f); load(f); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] bg-white max-w-[220px]"
            data-testid="backlog-project-select"
          >
            <option value="">Todos los proyectos</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          {/* Search */}
          <div className="relative">
            <input type="text" placeholder="Buscar..." value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm pl-3 pr-8 focus:outline-none focus:border-[#007934] w-36"
              data-testid="backlog-search" />
            {filters.search && (
              <button onClick={() => setFilters({ ...filters, search: "" })} className="absolute right-2 top-2.5 text-gray-400"><X size={12} /></button>
            )}
          </div>
          {/* More Filters */}
          <div className="relative">
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${hasFilters ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
              data-testid="backlog-filter-btn">
              <Filter size={14} /> Más filtros
              {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-white ml-0.5" />}
              <ChevronDown size={12} />
            </button>
            {showFilters && (
              <div className="absolute right-0 top-10 w-72 bg-white rounded-xl border border-gray-200 shadow-xl z-30 p-4 space-y-3" data-testid="backlog-filter-panel">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Proyecto</label>
                  <select value={filters.project_id} onChange={e => setFilters({ ...filters, project_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    <option value="">Todos</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Sprint</label>
                  <select value={filters.sprint_id} onChange={e => setFilters({ ...filters, sprint_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="backlog-sprint-filter">
                    <option value="">Todos</option>
                    <option value="__none__">Sin sprint asignado</option>
                    {sprints.map(s => (
                      <option key={s.id} value={s.id}>
                        Sprint {s.number}: {s.name}{s.project_title ? ` (${s.project_title})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Prioridad</label>
                  <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    <option value="">Todas</option>
                    <option value="critica">Crítica</option>
                    <option value="alta">Alta</option>
                    <option value="media">Media</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Fuente</label>
                  <select value={filters.source} onChange={e => setFilters({ ...filters, source: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    <option value="">Todas</option>
                    <option value="backlog_csv">Backlog Real (CSV)</option>
                    <option value="backlog_v2">Backlog v2 (Excel)</option>
                    <option value="azure_devops">Azure DevOps</option>
                  </select>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={resetFilters} className="flex-1 text-sm text-gray-500 border border-gray-200 rounded-lg py-2 hover:bg-gray-50" data-testid="backlog-filter-reset">Resetear</button>
                  <button onClick={() => applyFilters(filters)} className="flex-1 text-sm bg-[#007934] text-white rounded-lg py-2 font-medium hover:bg-[#006528]" data-testid="backlog-filter-apply">Aplicar</button>
                </div>
              </div>
            )}
          </div>
          <button onClick={() => load(filters)} className="p-2 text-gray-400 hover:text-gray-600 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-all">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* Iter313/P01 — el Kanban sigue limitando la carga a 2.000 tarjetas
          por rendimiento (no tiene sentido renderizar 7.260 a la vez), pero
          los contadores de arriba YA reflejan la población real completa y
          el buscador encuentra cualquier ítem exista o no en este corte. */}
      {truncatedInfo && !filters.search && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800" data-testid="backlog-truncated-banner">
          <Filter size={15} className="flex-shrink-0 text-red-600" />
          <span>
            El tablero Kanban muestra sólo <strong>{truncatedInfo.shown.toLocaleString("es-ES")}</strong> de <strong>{truncatedInfo.total.toLocaleString("es-ES")}</strong> work items reales por rendimiento.
            Los contadores de arriba sí reflejan el total real. Usa el <strong>buscador</strong> para encontrar cualquier ítem, esté o no en este corte, o filtra por <strong>proyecto/sprint</strong> para reducir el volumen.
          </span>
        </div>
      )}

      {/* P01 · resultado de búsqueda server-side — cubre TODA la población
          filtrada, no sólo los ítems ya cargados en el Kanban. */}
      {filters.search && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800" data-testid="backlog-search-result-banner">
          <Filter size={15} className="flex-shrink-0 text-blue-600" />
          {searching ? (
            <span>Buscando en toda la población filtrada…</span>
          ) : (
            <span>
              <strong>{searchResult?.total_matches ?? 0}</strong> coincidencia(s) reales para &ldquo;{filters.search}&rdquo;
              {searchResult?.truncated && <> — mostrando las primeras {searchResult.items.length}, refina la búsqueda para ver el resto</>}.
            </span>
          )}
        </div>
      )}

      {/* Volume warning — when no project selected and many items */}
      {!filters.project_id && items.length > 300 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
          <Filter size={15} className="flex-shrink-0 text-amber-600" />
          <span>
            Mostrando <strong>{items.length} work items</strong> de todos los proyectos.
            Selecciona un <strong>proyecto</strong> en el selector de arriba para filtrar la vista (recomendado).
          </span>
          <button onClick={() => { const oraId = projects.find(p => p.id === "ora-zaragoza-2026")?.id; if (oraId) { const f = { ...filters, project_id: oraId }; setFilters(f); load(f); } }} className="ml-auto text-xs underline whitespace-nowrap text-amber-700 hover:text-amber-900">Ver ORA Zaragoza</button>
        </div>
      )}

      {/* Active filters */}
      {hasFilters && (
        <div className="flex items-center gap-2 flex-wrap text-sm bg-[#E8F5E9] border border-[#C8E6C9] text-[#007934] px-4 py-2 rounded-lg">
          <Filter size={12} />
          <span className="text-xs">Filtros activos:</span>
          {filters.project_id && <span className="bg-white px-2 py-0.5 rounded-full text-xs">{projects.find(p => p.id === filters.project_id)?.title}</span>}
          {filters.sprint_id && (
            <span className="bg-white px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
              <GitBranch size={10} />
              {filters.sprint_id === "__none__" ? "Sin sprint" : (() => { const s = sprints.find(s => s.id === filters.sprint_id); return s ? `Sprint ${s.number}: ${s.name}` : filters.sprint_id; })()}
            </span>
          )}
          {filters.priority && <span className="bg-white px-2 py-0.5 rounded-full text-xs capitalize">{filters.priority}</span>}
          {filters.source && <span className="bg-white px-2 py-0.5 rounded-full text-xs">{filters.source}</span>}
          <button onClick={resetFilters} className="ml-auto text-xs underline">Limpiar</button>
        </div>
      )}

      {/* Kanban Board */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 size={28} className="animate-spin text-[#007934]" /></div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="backlog-kanban-board">
          {KANBAN_COLS.map(col => {
            const colItems = displayItems
              .filter(w => col.statuses.includes(w.status))
              .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9));
            // P01 · el contador de la cabecera de columna usa /backlog/stats
            // (población real completa) cuando no hay búsqueda activa — el
            // Kanban sólo renderiza un corte de colItems por rendimiento.
            const realColCount = (!filters.search && stats?.by_status)
              ? col.statuses.reduce((sum, s) => sum + (stats.by_status[s] || 0), 0)
              : colItems.length;
            return (
              <div key={col.key}
                className={`rounded-xl border-2 ${col.borderCls} bg-gray-50 min-h-[200px]`}
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(col.key)}
                data-testid={`backlog-col-${col.key}`}>
                {/* Column header */}
                <div className={`flex items-center justify-between px-3 py-2.5 rounded-t-xl border-b ${col.borderCls} ${col.headerCls}`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${col.dotCls}`} />
                    <span className="text-xs font-semibold">{col.label}</span>
                  </div>
                  <span className="text-xs font-bold" data-testid={`backlog-col-count-${col.key}`}>
                    {realColCount.toLocaleString("es-ES")}
                    {realColCount !== colItems.length && <span className="font-normal text-[10px] opacity-70"> ({colItems.length} mostrados)</span>}
                  </span>
                </div>
                {/* Cards */}
                <div className="p-2 space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto">
                  {colItems.map(wi => {
                    const Icon = typeIcon[wi.type] || CheckSquare;
                    const isReal = wi.source === "backlog_csv" || wi.source === "backlog_v2";
                    const hasSprint = wi.sprint_id && wi.sprint_name;
                    return (
                      <div key={wi.id} draggable onDragStart={() => setDragId(wi.id)} onDragEnd={() => setDragId(null)}
                        className={`bg-white rounded-lg p-2.5 cursor-grab active:cursor-grabbing hover:shadow-sm transition-all border ${isReal ? "border-amber-200 bg-amber-50/40" : "border-gray-100"} ${dragId === wi.id ? "opacity-40" : ""}`}
                        data-testid={`backlog-card-${wi.id}`}>
                        {/* Top badges */}
                        <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                          {isReal && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide">Backlog Real</span>}
                          {hasSprint && (
                            <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5" data-testid={`sprint-badge-${wi.id}`}>
                              <GitBranch size={8} /> S{wi.sprint_number}
                            </span>
                          )}
                        </div>
                        {wi.project_title && <p className="text-[10px] text-gray-400 mb-1 truncate">{wi.project_title}</p>}
                        <div className="flex items-start gap-1.5 mb-2">
                          <div className="w-5 h-5 rounded bg-[#E8F5E9] flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Icon size={11} className="text-[#007934]" />
                          </div>
                          <p className="text-xs font-medium text-gray-900 leading-tight line-clamp-2">{wi.title}</p>
                        </div>
                        {isReal && wi.description && (
                          <p className="text-[10px] text-gray-500 leading-relaxed mb-2 line-clamp-2">{wi.description}</p>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${priorityBadge[wi.priority] || "bg-gray-100 text-gray-500"}`}>{wi.priority}</span>
                            {wi.type === "Bug" && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">Bug</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            {wi.estimated_hours > 0 && <span className="text-[10px] text-gray-400">{wi.estimated_hours}h</span>}
                            {wi.assigned_to && wi.assigned_to !== "Pte. asignar"
                              ? <div className="w-5 h-5 rounded-full bg-[#007934]/10 flex items-center justify-center text-[10px] font-bold text-[#007934]" title={wi.assigned_to}>{wi.assigned_to.split(" ").map(n => n[0]).join("").slice(0,2).toUpperCase()}</div>
                              : <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">?</div>
                            }
                          </div>
                        </div>
                        {(wi.contrato || wi.area_responsable || wi.responsable_tecnico) && (
                          <div className="mt-1.5 pt-1.5 border-t border-gray-100 space-y-0.5">
                            {wi.contrato && <span className="text-[9px] text-gray-400 block">{wi.contrato}{wi.area_responsable ? ` · ${wi.area_responsable}` : ""}</span>}
                            {wi.responsable_tecnico && <span className="text-[9px] text-blue-400 block">{wi.responsable_tecnico}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {colItems.length === 0 && <div className="text-center py-8 text-gray-300 text-xs">Arrastra aquí</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
